import { sequentialPairs, } from "@knyt/artisan";
/**
 * A controller that manages an event listener on an element.
 *
 * @remarks
 *
 * This controller should be added before the host is connected.
 *
 * @example
 *
 * ```ts
 * import { createReference } from "@knyt/artisan";
 * import { EventListenableObserver } from "@knyt/tasker";
 *
 * new EventListenableObserver({
 *   eventName: "click",
 *   listener(event) {
 *     console.info("click event", event);
 *   },
 *   target$: createReference(document.body),
 * });
 *
 * @public
 */
export class EventListenableObserver {
    #isSetup = false;
    #eventName;
    #listener;
    #eventOptions;
    #listenable$;
    constructor({ eventName, listener, target$: listenable$, options: eventOptions, }) {
        this.#eventName = eventName;
        this.#listener = listener;
        this.#eventOptions = eventOptions;
        this.#listenable$ = listenable$;
        listenable$.subscribe(this);
    }
    #targetObserver = sequentialPairs(null, ([previousTarget, currentTarget]) => {
        if (previousTarget) {
            this.#teardownTarget(previousTarget);
        }
        if (currentTarget) {
            this.#setupTarget(currentTarget);
        }
    });
    next(nextTarget) {
        this.#targetObserver.next(nextTarget);
    }
    #setupTarget(target) {
        if (this.#isSetup)
            return;
        target.addEventListener(this.#eventName, 
        // Typecast due to incompatible types
        this.#listener, this.#eventOptions);
        this.#isSetup = true;
    }
    #teardownTarget(target) {
        if (!this.#isSetup)
            return;
        target.removeEventListener(this.#eventName, 
        // Typecast due to incompatible types
        this.#listener, this.#eventOptions);
        this.#isSetup = false;
    }
    /**
     * Adds the event listener to the target element.
     */
    setup() {
        const target = this.#listenable$.value;
        if (target) {
            this.#setupTarget(target);
        }
    }
    /**
     * Removes the event listener from the target element.
     */
    teardown() {
        const target = this.#listenable$.value;
        if (target) {
            this.#teardownTarget(target);
        }
    }
    dispose() {
        this.teardown();
    }
}
